import { useCallback, useEffect, useMemo, useState } from 'react'
import Sheet from './Sheet.jsx'
import {
  DEFAULT_TARGET, average, byDay, clockTime, deleteFast, duration, elapsedMs,
  endFast, getFasts, getOpenFast, hours, logFast, startFast, updateFast,
} from '../lib/fasting.js'
import { shortDate } from '../lib/format.js'

const R = 88
const C = 2 * Math.PI * R

/** One arc. A progress bar bent into a circle, and nothing about fasting is
    improved by decorating it. */
function Ring({ fraction, over }) {
  const f = Math.max(0, Math.min(1, fraction))
  return (
    <svg className="fast-ring" width="208" height="208" viewBox="0 0 208 208" aria-hidden="true">
      <circle className="track" cx="104" cy="104" r={R} />
      {f > 0 && (
        <circle
          className={'arc' + (over ? ' over' : '')}
          cx="104" cy="104" r={R}
          strokeDasharray={C}
          strokeDashoffset={C * (1 - f)}
        />
      )}
    </svg>
  )
}

export default function Fasting({ toast }) {
  const [open, setOpen] = useState(null)
  const [past, setPast] = useState(null)
  const [sheet, setSheet] = useState(null)
  /* Re-render once a minute. The figure is derived from the start time, so
     this only refreshes the reading — it is not what keeps time. */
  const [, tick] = useState(0)

  const load = useCallback(async () => {
    try {
      const [o, p] = await Promise.all([getOpenFast(), getFasts()])
      setOpen(o)
      setPast(p)
    } catch {
      setOpen(null)
      setPast([])
    }
  }, [])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!open) return undefined
    const id = setInterval(() => tick((n) => n + 1), 30000)
    return () => clearInterval(id)
  }, [open])

  const days = useMemo(() => byDay(past ?? [], 14), [past])
  const avg = useMemo(() => average(past ?? [], 7), [past])

  if (past === null) return null

  const lastEnded = past[0]?.ended_at
  /* A running fast carries its own target. Otherwise the one he last chose,
     then the one his last fast used, then the default. */
  const target = Number(open?.target_hours ?? localTarget() ?? past[0]?.target_hours ?? DEFAULT_TARGET)

  const ms = open ? elapsedMs(open) : null
  const done = open ? hours(ms) >= target : false
  const endsAt = open
    ? new Date(new Date(open.started_at).getTime() + target * 3600000)
    : null

  /* The chart scale leaves headroom above the target so a long fast still has
     somewhere to go, and the bars never touch the ceiling. */
  const top = Math.max(target * 1.25, ...days.map((d) => d.hours), 1)

  return (
    <div className="card fast">
      <div className="fast-top">
        <span className="lbl">Fasting</span>
        <button type="button" className="linkish" onClick={() => setSheet({ kind: 'target' })}>
          Target {target}h
        </button>
        {/* A fast he did not press the button for. Typing both ends after the
            fact is the normal case, not the exception. */}
        <button type="button" className="fast-add" aria-label="Log a past fast"
                onClick={() => setSheet({ kind: 'log' })}>
          ＋
        </button>
      </div>

      <Ring fraction={open ? hours(ms) / target : 0} over={done} />

      <div className="fast-dial">
        {open ? (
          <>
            <div className="big num">{duration(ms)}</div>
            <div className="sub">
              {done
                ? duration(ms - target * 3600000) + ' past ' + target + 'h'
                : 'of ' + target + 'h · ends ' + clockTime(endsAt.toISOString())}
            </div>
            <button type="button" className="linkish" disabled={open.id === 'pending'}
                    onClick={() => setSheet({ kind: 'edit', fast: open })}>
              Started {clockTime(open.started_at)} · edit
            </button>
          </>
        ) : (
          <>
            <div className="big num">
              {lastEnded ? duration(Date.now() - new Date(lastEnded).getTime()) : '—'}
            </div>
            <div className="sub">
              {lastEnded ? 'since your last fast ended' : 'no fasts yet'}
            </div>
          </>
        )}
      </div>

      {/*
        Optimistic. The ring changes on the tap and the write follows, because
        a second of nothing after pressing Start reads as a broken button and
        gets pressed again. If the write fails the screen goes back and says so.
      */}
      <button
        type="button"
        className={'btn' + (open ? ' ghost' : '')}
        onClick={async () => {
          if (open) {
            const was = open
            const endedAt = new Date().toISOString()
            setOpen(null)
            setPast((rows) => [{ ...was, ended_at: endedAt }, ...rows])
            try {
              const row = await endFast(was, endedAt)
              setPast((rows) => [row, ...rows.slice(1)])
              toast?.('Fast ended at ' + duration(ms))
            } catch (e) {
              setPast((rows) => rows.slice(1))
              setOpen(was)
              toast?.(e.message || 'Could not end that.')
            }
          } else {
            const startedAt = new Date().toISOString()
            setOpen({ id: 'pending', started_at: startedAt, ended_at: null, target_hours: target })
            try {
              setOpen(await startFast({ startedAt, targetHours: target }))
            } catch (e) {
              setOpen(null)
              toast?.(e.message || 'Could not start that.')
            }
          }
        }}
      >
        {open ? 'End fast' : 'Start fasting'}
      </button>

      <div className="fast-meta">
        <div>
          <span className="lbl">Target</span>
          <div className="v num">{target}h</div>
        </div>
        <div>
          <span className="lbl">{open ? 'Left' : 'Last fast'}</span>
          <div className="v num">
            {open
              ? (done ? '—' : duration(target * 3600000 - ms))
              : (past[0] ? duration(elapsedMs(past[0])) : '—')}
          </div>
        </div>
        <div>
          <span className="lbl">7-day avg</span>
          <div className="v num">{avg == null ? '—' : avg.toFixed(1) + 'h'}</div>
        </div>
      </div>

      {past.length > 0 && (
        <>
          <div className="fast-chartwrap">
            <span className="lbl">Last 14 days</span>
            <div className="fast-chart">
              <div className="goal" style={{ bottom: (target / top) * 100 + '%' }}>
                <span>{target}h</span>
              </div>
              {days.map((d) => (
                <div className={'bar' + (d.met ? '' : ' miss')} key={d.key}
                     title={shortDate(d.key) + ' · ' + d.hours.toFixed(1) + 'h'}>
                  <i style={{ height: Math.min(100, (d.hours / top) * 100) + '%' }} />
                </div>
              ))}
            </div>
            <div className="fast-days">
              {days.map((d, i) => (
                <span key={d.key}>{i % 2 === 0 || i === days.length - 1 ? d.date.getDate() : ''}</span>
              ))}
            </div>
          </div>

          <div className="fast-rows">
            {past.slice(0, 8).map((f) => {
              const len = elapsedMs(f)
              const met = hours(len) >= Number(f.target_hours)
              return (
                <button type="button" className="fast-row" key={f.id}
                        onClick={() => setSheet({ kind: 'edit', fast: f })}>
                  <span className="when">
                    <b>{shortDate(f.ended_at.slice(0, 10))}</b>
                    <span className="lbl">
                      {clockTime(f.started_at)} — {clockTime(f.ended_at)}
                    </span>
                  </span>
                  <span className={'dur num' + (met ? '' : ' miss')}>{duration(len)}</span>
                  <span className="chev" aria-hidden="true">›</span>
                </button>
              )
            })}
          </div>
        </>
      )}

      {sheet?.kind === 'target' && (
        <TargetSheet
          initial={target}
          onClose={() => setSheet(null)}
          onSave={async (v) => {
            setSheet(null)
            try {
              /* Changing the target is a one-field write; no reason to
                 re-read the whole history to show it. */
              if (open) setOpen(await updateFast(open, { target_hours: v }))
              else { setLocalTarget(v); tick((n) => n + 1) }
            } catch (e) { toast?.(e.message || 'Could not save that.') }
          }}
        />
      )}

      {sheet?.kind === 'log' && (
        <LogSheet
          target={target}
          onClose={() => setSheet(null)}
          onSave={async (vals) => {
            setSheet(null)
            try {
              const row = await logFast({ ...vals, targetHours: target })
              setPast((rows) => [row, ...rows]
                .sort((a, b) => (a.started_at < b.started_at ? 1 : -1)))
            } catch (e) { toast?.(e.message || 'Could not save that.') }
          }}
        />
      )}

      {sheet?.kind === 'edit' && (
        <EditSheet
          fast={sheet.fast}
          onClose={() => setSheet(null)}
          onSave={async (patch) => {
            setSheet(null)
            try {
              const row = await updateFast(sheet.fast, patch)
              if (row.ended_at) {
                setPast((rows) => rows.map((r) => (r.id === row.id ? row : r)))
                if (open?.id === row.id) setOpen(null)
              } else setOpen(row)
            } catch (e) { toast?.(e.message || 'Could not save that.') }
          }}
          onDelete={async () => {
            const id = sheet.fast.id
            setSheet(null)
            try {
              await deleteFast(id)
              setPast((rows) => rows.filter((r) => r.id !== id))
              if (open?.id === id) setOpen(null)
            } catch (e) { toast?.(e.message || 'Could not delete that.') }
          }}
        />
      )}
    </div>
  )
}

/* The next fast's target when none is running. A preference, not data, so it
   belongs in the browser rather than in a settings table — and if the browser
   refuses, the last fast's target is a fine answer. */
function setLocalTarget(v) {
  try { localStorage.setItem('fast.target', String(v)) } catch { /* private mode */ }
}
function localTarget() {
  try {
    const v = Number(localStorage.getItem('fast.target'))
    return v > 0 ? v : null
  } catch { return null }
}

function TargetSheet({ initial, onClose, onSave }) {
  const [v, setV] = useState(String(initial))
  return (
    <Sheet title="Target" onClose={onClose}>
      <div className="field">
        <label className="lbl" htmlFor="f-target">Hours</label>
        <input id="f-target" inputMode="decimal" value={v}
               onChange={(e) => setV(e.target.value)} />
      </div>
      <p className="hint" style={{ margin: '0 0 4px' }}>
        Kept on each fast, so changing it now does not rewrite what past fasts
        were aiming at.
      </p>
      <div className="sheet-actions">
        <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="btn"
                onClick={() => onSave(Math.max(1, Number(v) || DEFAULT_TARGET))}>
          Save
        </button>
      </div>
    </Sheet>
  )
}

/* Local datetime in and out. A datetime-local input speaks the phone's clock,
   the database speaks UTC, and confusing the two is how a fast ends up an
   eight-hour lie. */
const toLocal = (iso) => {
  const d = new Date(iso)
  const off = d.getTimezoneOffset() * 60000
  return new Date(d.getTime() - off).toISOString().slice(0, 16)
}
const fromLocal = (s) => (s ? new Date(s).toISOString() : null)

/* Both ends typed. Defaults to a plausible window ending now, so the common
   case is two taps on the pickers rather than four fields from scratch. */
function LogSheet({ target, onClose, onSave }) {
  const now = new Date()
  const then = new Date(now.getTime() - target * 3600000)
  const [start, setStart] = useState(toLocal(then.toISOString()))
  const [end, setEnd] = useState(toLocal(now.toISOString()))
  const bad = !start || !end || new Date(end) <= new Date(start)
  const len = bad ? null : duration(new Date(end) - new Date(start))

  return (
    <Sheet title="Log a past fast" onClose={onClose}>
      <div className="field">
        <label className="lbl" htmlFor="l-start">Started</label>
        <input id="l-start" type="datetime-local" value={start}
               onChange={(e) => setStart(e.target.value)} />
      </div>
      <div className="field">
        <label className="lbl" htmlFor="l-end">Ended</label>
        <input id="l-end" type="datetime-local" value={end}
               onChange={(e) => setEnd(e.target.value)} />
      </div>
      <p className="hint" style={{ margin: '0 0 4px', color: bad ? 'var(--danger)' : undefined }}>
        {bad ? 'The end has to come after the start.' : len + ' against a ' + target + 'h target'}
      </p>
      <div className="sheet-actions">
        <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="btn" disabled={bad}
                onClick={() => onSave({ startedAt: fromLocal(start), endedAt: fromLocal(end) })}>
          Save
        </button>
      </div>
    </Sheet>
  )
}

function EditSheet({ fast, onClose, onSave, onDelete }) {
  const [start, setStart] = useState(toLocal(fast.started_at))
  const [end, setEnd] = useState(fast.ended_at ? toLocal(fast.ended_at) : '')
  const bad = end && new Date(end) <= new Date(start)

  return (
    <Sheet title={fast.ended_at ? 'Edit fast' : 'Running fast'} onClose={onClose}>
      <div className="field">
        <label className="lbl" htmlFor="f-start">Started</label>
        <input id="f-start" type="datetime-local" value={start}
               onChange={(e) => setStart(e.target.value)} />
      </div>
      {fast.ended_at && (
        <div className="field">
          <label className="lbl" htmlFor="f-end">Ended</label>
          <input id="f-end" type="datetime-local" value={end}
                 onChange={(e) => setEnd(e.target.value)} />
        </div>
      )}
      {bad && <p className="hint" style={{ color: 'var(--danger)' }}>The end has to come after the start.</p>}
      <div className="sheet-actions">
        <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
        <button type="button" className="btn" disabled={!!bad}
                onClick={() => onSave(
                  fast.ended_at
                    ? { started_at: fromLocal(start), ended_at: fromLocal(end) }
                    : { started_at: fromLocal(start) },
                )}>
          Save
        </button>
      </div>
      <button type="button" className="btn ghost wide" onClick={onDelete}>
        Delete this fast
      </button>
    </Sheet>
  )
}
